import { twMerge } from "tailwind-merge";
import { useToolLabels } from "./ToolLabelsContext";

export interface ToolCallIndicatorProps {
  toolName: string;
  /**
   * Args the agent passed to the tool. Required to label generic MCP
   * tools (`search`, `call`, `describe`) meaningfully — without args
   * we'd render "Searching..." for every `search` invocation regardless
   * of what's being searched.
   */
  args?: Record<string, unknown>;
  state: "running" | "complete" | "error";
  className?: string;
}

/**
 * Humanize a raw identifier for the final fallback path. Snake_case →
 * Sentence Case. Only fires when consumers haven't supplied a label
 * via `ToolLabelsProvider`.
 */
function humanize(identifier: string): string {
  return identifier
    .replace(/[_.]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Look up the friendly label for a resource. Returns the registered
 * label if the consumer's `ToolLabelsProvider` carries it; otherwise
 * humanizes the registry name (`ad_campaigns` → "ad campaigns").
 */
function resourceLabel(
  resource: string | undefined,
  resources: Record<string, string> | undefined
): string {
  if (!resource) return "your data";
  return resources?.[resource] ?? resource.replace(/_/g, " ");
}

/**
 * MCP `call`: read operation_id from args, look up the operation label.
 *
 * Fallback chain:
 *   1. Exact operation_id match in `labels.operations`.
 *   2. "Working on <resource label>" derived from the resource prefix.
 *   3. null when no operation_id has arrived yet — the indicator stays
 *      hidden rather than showing a generic placeholder that flashes
 *      for a moment and then gets replaced. Less visually noisy; the
 *      preceding agent text ("Saving the geometric dome with the
 *      lights now.") already tells the user what's happening.
 */
function labelForMcpCall(
  args: Record<string, unknown> | undefined,
  operations: Record<string, string> | undefined,
  resources: Record<string, string> | undefined
): string | null {
  const operationId = args?.operation_id;
  if (typeof operationId !== "string") return null;

  if (operations?.[operationId]) return operations[operationId]!;

  const [resource] = operationId.split(".");
  return `Working on ${resourceLabel(resource, resources)}`;
}

/**
 * MCP `search`: read `resource` from args, optionally inline `q.query`.
 *
 * Streaming gap → null (no indicator). Once `resource` resolves we
 * show "Searching <resource label>" (with the query inlined for
 * free-text searches).
 */
function labelForMcpSearch(
  args: Record<string, unknown> | undefined,
  resources: Record<string, string> | undefined,
  tense: LabelTense
): string | null {
  const resource =
    typeof args?.resource === "string" ? args.resource : undefined;
  if (!resource) return null;

  const q = args?.q as Record<string, unknown> | undefined;
  const verb = tense === "past" ? "Searched" : "Searching";

  // For free-text searches (resources whose `q` carries a `query`
  // field), surface the query so the user sees what's being looked up.
  // For predicate-based searches (q.is_logo_eq=true), generic
  // "Searching <resource>" is honest — predicate vocabulary doesn't
  // read well to non-technical users.
  if (typeof q?.query === "string" && q.query.trim()) {
    const truncated = q.query.length > 40 ? `${q.query.slice(0, 40)}…` : q.query;
    return `${verb} ${resourceLabel(resource, resources)} for "${truncated}"`;
  }

  return `${verb} ${resourceLabel(resource, resources)}`;
}

function labelForMcpDescribe(
  args: Record<string, unknown> | undefined,
  resources: Record<string, string> | undefined,
  tense: LabelTense
): string | null {
  const resource =
    typeof args?.resource === "string" ? args.resource : undefined;
  if (!resource) return null;
  const verb = tense === "past" ? "Researched" : "Researching";
  return `${verb} ${resourceLabel(resource, resources)} options`;
}

/**
 * Pick the most specific label we can for a tool invocation.
 *
 * Priority:
 *   1. MCP universal tools (`search`, `call`, `describe`,
 *      `list_resources`) → inspect args + the resources/operations
 *      labels from context.
 *   2. Bespoke tool name in `labels.tools` → static label.
 *   3. Fallback → humanized tool name (snake_case → Sentence Case).
 *
 * Returns null when the indicator should render nothing — the mid-stream
 * gap before args have streamed in. Showing a generic "Working on it"
 * placeholder stutters: it flashes, then gets replaced ~100ms later.
 * Better to stay quiet until we can say something specific; the
 * agent's preceding text usually covers the intent anyway.
 */
export type LabelTense = "present" | "past";

export function labelForTool(
  toolName: string,
  args: Record<string, unknown> | undefined,
  labels: {
    resources?: Record<string, string>;
    operations?: Record<string, string>;
    tools?: Record<string, string>;
  },
  /**
   * "present" while the call runs ("Searching stock photos…"),
   * "past" once it settles ("Searched stock photos"). Operation labels
   * (`labels.operations`) are consumer-authored strings and render
   * verbatim in either tense.
   */
  tense: LabelTense = "present"
): string | null {
  switch (toolName) {
    case "search":
      return labelForMcpSearch(args, labels.resources, tense);
    case "call":
      return labelForMcpCall(args, labels.operations, labels.resources);
    case "describe":
      return labelForMcpDescribe(args, labels.resources, tense);
    case "list_resources":
      return tense === "past"
        ? "Checked what I have access to"
        : "Checking what I have access to";
    default:
      // Bespoke tools have their name from the start (no streaming
      // gap), so render immediately.
      return labels.tools?.[toolName] ?? humanize(toolName);
  }
}

export function ToolCallIndicator({
  toolName,
  args,
  state,
  className,
}: ToolCallIndicatorProps) {
  const labels = useToolLabels();
  if (state !== "running") return null;

  const label = labelForTool(toolName, args, labels);
  if (label === null) return null;

  return (
    <div
      className={twMerge(
        "flex items-center gap-2 py-1 text-sm text-base-400 italic",
        className
      )}
      role="status"
      aria-label={`Running: ${label}`}
    >
      <span className="inline-block h-2 w-2 rounded-full bg-primary-500 animate-pulse" />
      <span>{label}...</span>
    </div>
  );
}
